// ModuleParts — a module's parts in reading order, in the material detail,
// pure: the same numbered parts joined by the accent seam as the Studio's
// module card, each a button that opens that part.
//
// A module is a material whose content includes other resources (`include`
// nodes, server-projected as `parts`). The editor has no block for an include,
// so without this a module read as an empty material.

export type { ModulePart };
export { ModuleParts, projectModuleParts };

import { useLingui } from "@lingui/react/macro";
import { Fragment, type ReactNode } from "react";
import type { Library } from "../../app/ports/workspace.js";
import "./ModuleParts.css";

type ModulePart = { readonly id: string; readonly title: string };

// The material's parts resolved against the Library, in reading order. A part
// no longer in the workspace is left out; one without a title shows
// `untitled`.
const projectModuleParts = (
    library: Library | null,
    materialId: string | null,
    untitled: string,
): readonly ModulePart[] => {
    if (library === null || materialId === null) return [];
    const ids = library.materials.find((m) => m.id === materialId)?.parts ?? [];
    const titles = new Map<string, string>(
        [...library.materials, ...library.sources, ...library.files].map(
            (r) => [r.id, r.title ?? untitled],
        ),
    );
    return ids.flatMap((id) => {
        const title = titles.get(id);
        return title === undefined ? [] : [{ id, title }];
    });
};

const ModuleParts = ({
    parts,
    onOpen,
}: {
    readonly parts: readonly ModulePart[];
    readonly onOpen: (id: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <section className="bilda-module-parts" aria-label={t`Parts`}>
            <h3 className="bilda-module-parts__heading">{t`Parts`}</h3>
            <ol className="bilda-module-parts__list">
                {parts.map((part, i) => (
                    <Fragment key={part.id}>
                        {i > 0 ? (
                            <li
                                className="bilda-module-parts__seam"
                                aria-hidden="true"
                            />
                        ) : null}
                        <li>
                            <button
                                type="button"
                                className="bilda-module-parts__part"
                                onClick={() => onOpen(part.id)}
                            >
                                <span className="bilda-module-parts__number">
                                    {i + 1}
                                </span>
                                <span className="bilda-module-parts__title">
                                    {part.title}
                                </span>
                            </button>
                        </li>
                    </Fragment>
                ))}
            </ol>
        </section>
    );
};
